import React, { ReactNode, useMemo, useState } from "react";
import { ArrowUpRightIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import Prism from "prismjs";
import "prismjs/components/prism-bash";
import "prismjs/components/prism-yaml";
import "prismjs/themes/prism-okaidia.css";
import type { CreateFirewallRule } from "components/Environment/Create/ExposePorts";
import type { ContainerSelected } from "components/Environment/shared/BuildTypes";
import {
  classNames,
  githubRepoRegexCheck,
  gitlabRepoRegexCheck,
} from "components/utils";

interface LaunchableConfigurationContentProps {
  file?: string | null;
  lifecycleScript?: string | null;
  containerSelected?: ContainerSelected | null;
  imageId?: string | null;
  ports?: Record<string, string> | null;
  firewallRules?: CreateFirewallRule[] | null;
}

interface CollapsibleConfigurationProps {
  title: string;
  description: string;
  children: ReactNode;
  defaultOpen?: boolean;
}

const CollapsibleConfiguration: React.FC<CollapsibleConfigurationProps> = ({
  title,
  description,
  children,
  defaultOpen = false,
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="overflow-hidden rounded-md border border-zinc-800 bg-black">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-4 px-4 py-3 text-left outline-none transition-colors hover:bg-zinc-900/70 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-highlight"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((current) => !current)}
      >
        <span>
          <span className="block text-sm font-semibold text-white">
            {title}
          </span>
          <span className="mt-0.5 block text-xs text-secondary">
            {description}
          </span>
        </span>
        <ChevronDownIcon
          className={classNames(
            "h-4 w-4 shrink-0 text-zinc-500 transition-transform",
            isOpen ? "rotate-180" : "",
          )}
          aria-hidden="true"
        />
      </button>

      {isOpen && <div className="border-t border-zinc-800">{children}</div>}
    </div>
  );
};

const HighlightedCode: React.FC<{
  code: string;
  language: "bash" | "yaml";
}> = ({ code, language }) => {
  const highlightedCode = useMemo(
    () => Prism.highlight(code, Prism.languages[language], language),
    [code, language],
  );
  const lineNumbers = useMemo(
    () =>
      Array.from(
        { length: code.split("\n").length },
        (_, index) => index + 1,
      ).join("\n"),
    [code],
  );

  return (
    <div className="flex min-w-0 text-sm leading-6">
      <pre
        className="shrink-0 select-none border-r border-zinc-800 bg-zinc-950 px-3 py-4 text-right text-zinc-600"
        aria-hidden="true"
      >
        {lineNumbers}
      </pre>
      <pre className="min-w-0 flex-1 whitespace-pre-wrap break-all bg-black px-4 py-4 text-white">
        <code
          className={`language-${language} !whitespace-pre-wrap !break-all !leading-6`}
          dangerouslySetInnerHTML={{ __html: highlightedCode }}
        />
      </pre>
    </div>
  );
};

const SOFTWARE_CONFIGURATION_DOCS_URL =
  "https://docs.nvidia.com/brev/concepts/launchables#software-configuration";

const getRepositoryInfo = (
  file?: string | null,
): { name: string; url: string } | null => {
  if (!file) return null;

  try {
    const url = new URL(file);
    const pathParts = url.pathname.split("/").filter(Boolean);

    if (githubRepoRegexCheck(file) && pathParts.length >= 2) {
      return {
        name: pathParts[1].replace(/\.git$/, ""),
        url: file,
      };
    }

    if (gitlabRepoRegexCheck(file) && pathParts.length >= 2) {
      const repositoryName = pathParts.at(-1);

      if (repositoryName) {
        return {
          name: repositoryName.replace(/\.git$/, ""),
          url: file,
        };
      }
    }
  } catch {
    return null;
  }

  return null;
};

const getBuildDetails = (
  containerSelected?: ContainerSelected | null,
): { label: string; description: string } => {
  if (containerSelected?.dockerCompose) {
    return {
      label: "Docker Compose",
      description:
        "Services are created from the Compose configuration provided by the author.",
    };
  }

  if (containerSelected?.customContainer) {
    return {
      label: "Single Container",
      description:
        "The environment starts from the Docker image configured by the author.",
    };
  }

  if (
    containerSelected?.vmBuild?.k8s &&
    !containerSelected.vmBuild.k8s.isDisabled
  ) {
    return {
      label: "Single-node Kubernetes",
      description:
        "A single-node Kubernetes cluster is configured on the virtual machine.",
    };
  }

  if (containerSelected?.vmBuild) {
    return {
      label: "VM mode",
      description:
        "Software and startup configuration run directly on the host.",
    };
  }

  return {
    label: "Single Container",
    description:
      "Brev builds the environment from the author's software configuration.",
  };
};

const BuildDescription: React.FC<{
  containerSelected?: ContainerSelected | null;
}> = ({ containerSelected }) => {
  const { label, description } = getBuildDetails(containerSelected);

  return (
    <>
      Deploying this launchable creates a Brev environment running{" "}
      <a
        href={SOFTWARE_CONFIGURATION_DOCS_URL}
        target="_blank"
        rel="noreferrer"
        className="font-medium text-highlight underline decoration-highlight/50 underline-offset-2 transition-colors hover:text-highlightLighter"
      >
        {label}
      </a>
      . {description}
    </>
  );
};

const NetworkConfiguration: React.FC<{
  ports?: Record<string, string> | null;
  firewallRules?: CreateFirewallRule[] | null;
}> = ({ ports, firewallRules }) => {
  const secureLinks = Object.entries(ports || {});
  const exposedPorts = firewallRules || [];
  const pillClass =
    "inline-flex items-center gap-2 rounded-md border border-zinc-700 bg-zinc-900/70 px-3 py-1.5 text-sm text-primary";

  if (secureLinks.length === 0 && exposedPorts.length === 0) {
    return null;
  }

  return (
    <div className="grid justify-items-start gap-5">
      {secureLinks.length > 0 && (
        <div className="w-full max-w-xl">
          <h3 className="mb-2 text-sm font-medium text-white">
            Secure links (HTTPS)
          </h3>
          <p className="mb-3 text-sm leading-5 text-secondary">
            The following services will be exposed over HTTPS from the instance.
          </p>
          <table className="w-full table-fixed text-left text-sm">
            <thead className="text-secondary">
              <tr className="border-b border-zinc-700">
                <th
                  className="w-3/5 border-r border-zinc-800 px-3 py-2 font-medium"
                  scope="col"
                >
                  Service name
                </th>
                <th className="px-3 py-2 font-medium" scope="col">
                  Exposed port
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {secureLinks.map(([name, port]) => (
                <tr key={name}>
                  <td className="break-words border-r border-zinc-800 px-3 py-2.5 font-medium text-primary">
                    {name}
                  </td>
                  <td className="px-3 py-2.5 font-mono text-secondary">
                    {port}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {exposedPorts.length > 0 && (
        <div className="w-full max-w-xl">
          <h3 className="mb-2 text-sm font-medium text-white">TCP/UDP ports</h3>
          <ul className="flex flex-wrap gap-2">
            {exposedPorts.map((rule, index) => {
              const access =
                rule.allowedIPs === "all" || rule.allowedIPs === "0.0.0.0/0"
                  ? "All IPs"
                  : rule.allowedIPs === "user-ip"
                    ? "Your IP"
                    : rule.allowedIPs || "Restricted";

              return (
                <li
                  key={`${rule.port}-${rule.allowedIPs}-${index}`}
                  className={pillClass}
                >
                  <span className="font-mono font-medium">{rule.port}</span>
                  <span className="h-3 w-px bg-zinc-700" aria-hidden="true" />
                  <span className="text-secondary">{access}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

const LaunchableConfigurationContent: React.FC<
  LaunchableConfigurationContentProps
> = ({
  file,
  lifecycleScript,
  containerSelected,
  imageId,
  ports,
  firewallRules,
}) => {
  const hasLifecycleScript = Boolean(lifecycleScript?.trim());
  const customContainer = containerSelected?.customContainer;
  const dockerCompose = containerSelected?.dockerCompose;
  const composeYaml = (
    dockerCompose?.yamlString || dockerCompose?.parsedYaml
  )?.trim();
  const composeFileUrl = dockerCompose?.fileUrl?.trim();
  const repository = getRepositoryInfo(file);
  const isSingleNodeKubernetes = Boolean(
    containerSelected?.vmBuild?.k8s &&
    !containerSelected.vmBuild.k8s.isDisabled,
  );
  const hasArtifacts = Boolean(
    customContainer || dockerCompose || hasLifecycleScript,
  );
  const deploymentAssetNames = [
    customContainer ? "a Docker container" : null,
    dockerCompose ? "Docker Compose services" : null,
    hasLifecycleScript
      ? isSingleNodeKubernetes
        ? "a cluster init script"
        : "a startup script"
      : null,
  ].filter((asset): asset is string => Boolean(asset));
  const formattedDeploymentAssetNames =
    deploymentAssetNames.length > 1
      ? `${deploymentAssetNames.slice(0, -1).join(", ")} and ${deploymentAssetNames.at(-1)}`
      : deploymentAssetNames[0];

  const configurationDetails = (
    <>
      {customContainer && (
        <CollapsibleConfiguration
          title="Docker container"
          description="Image and startup command configured by the author"
        >
          <dl className="grid gap-4 px-4 py-4 text-sm sm:grid-cols-[120px_minmax(0,1fr)]">
            <dt className="text-secondary">Image</dt>
            <dd className="break-all font-mono text-white">
              {customContainer.containerUrl}
            </dd>
            {customContainer.entryPoint && (
              <>
                <dt className="text-secondary">Entrypoint</dt>
                <dd className="break-all font-mono text-white">
                  {customContainer.entryPoint}
                </dd>
              </>
            )}
          </dl>
        </CollapsibleConfiguration>
      )}

      {dockerCompose && (
        <CollapsibleConfiguration
          title="Docker Compose"
          description="Runs automatically when the VM starts"
        >
          {composeFileUrl && (
            <div className="border-b border-zinc-800 px-4 py-3 text-xs">
              <a
                href={composeFileUrl}
                target="_blank"
                rel="noreferrer"
                className="break-all text-highlight underline underline-offset-2"
              >
                View source file
              </a>
            </div>
          )}
          {composeYaml ? (
            <HighlightedCode code={composeYaml} language="yaml" />
          ) : (
            <p className="px-4 py-4 text-sm text-secondary">
              The Compose configuration is provided by the linked source file.
            </p>
          )}
        </CollapsibleConfiguration>
      )}

      {hasLifecycleScript && (
        <CollapsibleConfiguration
          title={
            isSingleNodeKubernetes ? "Cluster init script" : "Startup script"
          }
          description={
            containerSelected?.vmBuild?.k8s &&
            !containerSelected.vmBuild.k8s.isDisabled
              ? "Runs after the single-node Kubernetes host is built"
              : "Runs automatically when the VM is built"
          }
        >
          <HighlightedCode code={lifecycleScript!} language="bash" />
        </CollapsibleConfiguration>
      )}
    </>
  );

  return (
    <section aria-label="Launchable configuration">
      <div className="mb-6">
        <p className="max-w-3xl text-sm leading-6 text-secondary">
          <BuildDescription containerSelected={containerSelected} />
        </p>
        {imageId && (
          <p className="mt-2 max-w-3xl text-sm leading-6 text-secondary">
            This launchable uses a machine image for faster start times.
          </p>
        )}
      </div>

      <div className="space-y-6">
        {repository && (
          <div>
            <h3 className="text-sm font-medium text-white">Source</h3>
            <p className="mt-2 text-sm leading-6 text-secondary">
              Clones{" "}
              <a
                href={repository.url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 font-semibold text-highlight underline decoration-highlight/50 underline-offset-2 outline-none transition-colors hover:text-highlightLighter focus-visible:ring-2 focus-visible:ring-highlight"
              >
                {repository.name}
                <ArrowUpRightIcon className="h-3.5 w-3.5" aria-hidden="true" />
              </a>{" "}
              to the VM.
            </p>
          </div>
        )}

        <NetworkConfiguration ports={ports} firewallRules={firewallRules} />

        {hasArtifacts && (
          <div>
            <h3 className="text-sm font-medium text-white">
              Deployment assets
            </h3>
            <p className="mt-2 text-sm leading-6 text-secondary">
              The launchable author has configured{" "}
              {formattedDeploymentAssetNames} to run automatically after the
              instance is running.
            </p>
            <div className="mt-4 space-y-3">{configurationDetails}</div>
          </div>
        )}
      </div>
    </section>
  );
};

export default LaunchableConfigurationContent;
